import React, {useEffect, useState} from 'react';
import {Calendar, Users, Heart, Star} from 'lucide-react';
import {Header} from "../components/Header";
import {t} from "i18next";
import {toast} from "react-toastify";
import {getAllShatayushiMembersData} from "../service/shatayushiMemberApi";
import Cookies from "universal-cookie";
import {Transition, TransitionChild} from "@headlessui/react";

export const ShatayushiMembers = () => {
    // Sample data - Replace with your actual details of Shatayushi Members
    const cookies = new Cookies();
    const locale = cookies.get("locale");

    const [shatayushiMembersDataLoading, setShatayushiMembersDataLoading] = useState(true);
    const [shatayushiMembers, setShatayushiMembers] = useState([]);

    const [showShatayushiMemberModal, setShowShatayushiMemberModal] = useState(false);
    const [selectedShatayushiMember, setSelectedShatayushiMember] = useState({
        age: 100,
        description: "",
        userId: "",
        dateOfBirth: "",
        dateOfDeath: "",
        familyTreeName: "",
        firstName: "",
        middleName: "",
        imageUrl: "",
        imageUrlBg: "",
    });

    const formatDate = (isoString) => {
        const date = new Date(isoString);

        const options = {year: "numeric", month: "long", day: "numeric"};
        return date.toLocaleDateString(locale === "en" ? "en-US" : "mr-IN", options);
    }

    const getAllShatayushiMembers = async () => {
        try {
            const response = await getAllShatayushiMembersData();
            console.log("(ShatayushiMembers.jsx) Get Shatayushi Members data Response: ", response);

            if (response.success) {
                // Format data received from the API
                const formattedMembers = response.data.map((member) => {
                    const isEnglish = locale === "en";

                    return {
                        age: isEnglish ? member.age : member.age.toLocaleString('mr-IN'),
                        description: member.description,
                        userId: member.userId,
                        dateOfBirth: formatDate(member.userDetails.dateOfBirth),
                        dateOfDeath: formatDate(member.userDetails.dateOfDeath),
                        familyTreeName: isEnglish ? member.userDetails.familyTreeName : member.userDetails.familyTreeNameDevanagariScript,
                        firstName: isEnglish ? member.userDetails.firstName : member.userDetails.firstNameDevanagariScript,
                        middleName: isEnglish ? member.userDetails.middleName : member.userDetails.middleNameDevanagariScript,
                        imageUrl: member.userDetails.imageUrl,
                        imageUrlBg: member.userDetails.imageUrlBg,
                    };
                });

                console.log("Formatted Shatayushi Members: ", formattedMembers);

                setShatayushiMembers(formattedMembers);
                setSelectedShatayushiMember(formattedMembers[0]);
            } else {
                setShatayushiMembers([]);
            }
            setShatayushiMembersDataLoading(false);
        } catch (error) {
            console.error("(ShatayushiMembers.jsx) Error fetching Shatayushi Members data: ", error);
            toast.error("Something went wrong while fetching Shatayushi Members data!");
            setShatayushiMembersDataLoading(false);
        }
    }

    useEffect(() => {
        console.log("(Events.jsx) Events Component Mounted!");
        getAllShatayushiMembers();

        return () => {
            console.log("(Events.jsx) Events Component Unmounted!");
        };
    }, []);

    if (shatayushiMembersDataLoading) {
        return (
            <div className={"w-[100dvw] h-[100dvh] bg-[#F7F7F7] flex items-center justify-center"}>
                <Header/>
                <h1 className={"text-[5vw] font-custom-mono-bold"}>
                    Loading...
                </h1>
            </div>
        )
    }

    return (
        <>
            <div className={"w-[100vw] bg-white flex flex-col items-center justify-center"}>
                <Header/>

                {/*
                    portrait:h-[2.5rem] portrait:p-[0.4rem] portrait:pl-[0.65rem]
                    portrait:sm:h-[4rem] portrait:sm:p-[0.65rem] portrait:sm:pl-[1.5rem]
                    portrait:jk:h-[3.5rem] portrait:jk:p-[0.5rem]

                    landscape:h-[2.5rem] landscape:p-[0.4rem]
                    landscape:jk:h-[3.5rem] landscape:jk:p-[0.4rem]

                    portrait:top-[0.75rem] portrait:inset-x-[0.75rem]
                    landscape:top-[0.5rem] landscape:inset-x-[0.5rem]
                */}

                <div className="min-h-screen w-screen bg-gradient-to-br from-slate-50 via-amber-50 to-rose-50">
                    {/* Header */}
                    <div
                        className="min-h-[100dvh] h-[100dvh] max-h-[100dvh]
                            bg-white/80 backdrop-blur-sm
                            border-b border-amber-200 sticky
                            -top-[72.75dvh] md:-top-[68.75dvh] z-10"
                    >
                        <img
                            src="/banners/banner-shatayushi.png"
                            alt="Shatayushi"
                            className="w-full h-[83dvh] md:h-[80dvh] object-cover shadow-lg"
                        />

                        <div
                            className="h-[17dvh] md:h-[20dvh] container mx-auto p-4 md:p-6
                                flex flex-col justify-center items-center gap-y-2"
                        >
                            <h1 className="text-center text-4xl md:text-5xl font-serif text-slate-800 font-custom-bold">
                                {t("SHATAYUSHI_PAGE.HEADING.TITLE")}
                            </h1>
                            <p className="text-center text-slate-600 text-lg font-custom-regular-italic">
                                {t("SHATAYUSHI_PAGE.HEADING.DESCRIPTION")}
                            </p>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="container mx-auto py-12">
                        {/* Introduction */}
                        <div className="text-center mb-16">
                            <div className="max-w-4xl mx-auto">
                                <Star className="h-8 w-8 text-amber-600 mx-auto mb-4"/>
                                <p className="text-xl text-slate-700 leading-relaxed font-custom-medium">
                                    {t("SHATAYUSHI_PAGE.SUVICHAAR")}
                                </p>
                            </div>
                        </div>

                        {/* ShatAyushi Members Grid */}
                        <div className="flex items-center justify-center flex-wrap gap-8 mb-16">
                            {
                                shatayushiMembers.length > 0 && (
                                    shatayushiMembers.map((person) => (
                                        <div
                                            key={person.userId}
                                            className="bg-white/90 backdrop-blur-sm rounded-2xl
                                                shadow-lg hover:shadow-xl
                                                transition-all duration-300 overflow-hidden
                                                cursor-pointer transform hover:-translate-y-1 w-[90vw] md:w-[24vw]"
                                            onClick={() => {
                                                setSelectedShatayushiMember(person);
                                                setShowShatayushiMemberModal(true);
                                            }}
                                        >
                                            <div className="relative">
                                                {person.imageUrl ? (
                                                    person.imageUrl.length <= 2 ? (
                                                        <div
                                                            className={`z-10 w-auto aspect-square overflow-hidden 
                                                            flex items-center justify-center ${person.imageUrlBg} 
                                                            border border-[#D4D4D4] shadow-lg shadow-black/30`}
                                                        >
                                                            <p
                                                                className="font-custom-mono-bold text-white leading-tight
                                                                line-clamp-1 overflow-x-scroll no-scrollbar
                                                                portrait:text-[4rem] portrait:md:text-[4.5rem] portrait:jk:text-[6rem]
                                                                landscape:text-[2.75rem] landscape:md:text-[3rem] landscape:jk:text-[4rem]"
                                                            >
                                                                {person.imageUrl.toUpperCase()}
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            className="z-10 w-auto aspect-square"
                                                            src={person.imageUrl}
                                                            alt="User Profile Image"
                                                            referrerPolicy={"no-referrer"}
                                                        />
                                                    )
                                                ) : (
                                                    <img
                                                        className="w-auto aspect-square bg-[#D26A53]"
                                                        src={"/assets/default-pfp.png"}
                                                        alt="Default PFP"
                                                        referrerPolicy={"no-referrer"}
                                                    />
                                                )}

                                                <div
                                                    className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"/>

                                                <div className="absolute bottom-4 left-4 right-4 text-white">
                                                    <h3 className="text-xl font-custom-medium mb-1">
                                                        {person.firstName + " " + person.middleName}
                                                    </h3>
                                                    <div
                                                        className="flex items-center text-sm opacity-90 font-custom-regular">
                                                        <Calendar className="h-4 w-4 mr-1"/>
                                                        <span>
                                                            {person.age} {t("SHATAYUSHI_PAGE.MEMBER_CARD.AGE")}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-6">
                                                <p className="text-slate-600 text-sm leading-relaxed line-clamp-3 font-custom-medium">
                                                    {person.description}
                                                </p>

                                                <div className="mt-4 pt-4 border-t border-slate-200">
                                                    <div
                                                        className="flex items-center  mb-2 font-custom-medium
                                                            text-sm text-slate-500"
                                                    >
                                                        <Users className="h-4 w-4 mr-2"/>
                                                        <span>{person.familyTreeName}</span>
                                                    </div>
                                                    <div className="text-xs text-slate-400 font-custom-regular">
                                                        {person.dateOfBirth} - {person.dateOfDeath}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )
                            }
                        </div>

                        {/* Memorial Quote */}
                        <div
                            className="bg-white/60 backdrop-blur-sm rounded-2xl p-8
                                text-center border border-amber-200"
                        >
                            <Heart className="h-8 w-8 text-rose-500 mx-auto mb-4"/>
                            <blockquote className="text-2xl font-serif text-slate-800 font-custom-medium-italic mb-4">
                                "{t("SHATAYUSHI_PAGE.FOOTER.TITLE")}"
                            </blockquote>
                            <p className="text-slate-600 font-custom-regular">
                                {t("SHATAYUSHI_PAGE.FOOTER.DESCRIPTION")}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <>
                {document.body.classList.toggle("overflow-y-hidden", showShatayushiMemberModal)}

                <Transition show={showShatayushiMemberModal}>
                    <div
                        className={`min-h-[100dvh] h-[100dvh] max-h-[100dvh] 
                            min-w-[100dvw] w-[100dvw] max-w-[100dvw]
                            flex items-center justify-center fixed inset-0 z-[100]
                            bg-black/75 backdrop-blur-sm data-[closed]:opacity-0
                            data-[enter]:duration-150 data-[enter]:data-[closed]:ease-out
                            data-[leave]:duration-300 data-[leave]:data-[closed]:ease-out`}
                    >
                        <TransitionChild
                            enter="transition-transform duration-300 ease-out"
                            enterFrom="translate-y-full"
                            enterTo="translate-y-0"
                            leave="transition-transform duration-300 ease-in"
                            leaveFrom="translate-y-0"
                            leaveTo="translate-y-full"
                        >
                            <div
                                className="min-h-fit h-fit max-h-[100dvh]
                                    landscape:min-w-[25vw] landscape:max-w-[90vw]

                                    portrait:min-h-full portrait:h-full portrait:max-h-full
                                    portrait:md:!min-h-fit portrait:md:!h-fit portrait:md:!max-h-fit

                                    portrait:min-w-[100dvw] portrait:w-[100dvw] portrait:max-w-[100dvw]
                                    portrait:md:min-w-[40vw] portrait:md:max-w-[90vw]

                                    transform z-50 rounded-xl flex items-center justify-center
                                    data-[enter]:duration-150 data-[enter]:data-[closed]:scale-75
                                    data-[leave]:duration-300 data-[leave]:data-[closed]:scale-95"
                            >
                                <div
                                    className="form-modal-container h-[90vh] max-h-[90vh] landscape:md:!flex-row
                                        portrait:md:!h-[90vh] portrait:md:!max-h-[90vh]

                                        landscape:md:min-h-[22.5rem] landscape:md:h-[22.5rem] landscape:md:max-h-[22.5rem]
                                        landscape:!min-w-[30vw] landscape:!max-w-fit

                                        portrait:!min-w-full portrait:!w-full portrait:!max-w-full
                                        portrait:md:!min-w-[60dvw] portrait:md:!w-[60dvw] portrait:md:!max-w-[60dvw]

                                        portrait:rounded-none portrait:md:rounded-lg landscape:rounded-lg"
                                >
                                    {/* Header | Picture */}
                                    <div className="relative">
                                        {selectedShatayushiMember.imageUrl ? (
                                            selectedShatayushiMember.imageUrl.length <= 2 ? (
                                                <div
                                                    className={`z-10 w-auto landscape:md:w-[22.5rem] aspect-square overflow-hidden 
                                                    flex items-center justify-center ${selectedShatayushiMember.imageUrlBg} 
                                                    border border-[#D4D4D4] shadow-lg shadow-black/30`}
                                                >
                                                    <p
                                                        className="font-custom-mono-bold text-white leading-tight
                                                        line-clamp-1 overflow-x-scroll no-scrollbar
                                                        portrait:text-[4rem] portrait:md:text-[4.5rem] portrait:jk:text-[6rem]
                                                        landscape:text-[2.75rem] landscape:md:text-[3rem] landscape:jk:text-[4rem]"
                                                    >
                                                        {selectedShatayushiMember.imageUrl.toUpperCase()}
                                                    </p>
                                                </div>
                                            ) : (
                                                <img
                                                    className="z-10 w-auto landscape:md:w-[22.5rem]  aspect-square"
                                                    src={selectedShatayushiMember.imageUrl}
                                                    alt={selectedShatayushiMember.firstName + " " + selectedShatayushiMember.middleName}
                                                    referrerPolicy={"no-referrer"}
                                                />
                                            )
                                        ) : (
                                            <img
                                                className="w-auto landscape:md:w-[22.5rem] aspect-square bg-[#D26A53]"
                                                src={"/assets/default-pfp.png"}
                                                alt="Default PFP"
                                                referrerPolicy={"no-referrer"}
                                            />
                                        )}
                                    </div>

                                    {/* Body | Details*/}
                                    <div
                                        className="form-modal-body-container
                                            !overflow-none landscape:md:!max-w-[30rem]"
                                    >
                                        <div className={"flex flex-row items-start justify-between"}>
                                            <h2 className="text-3xl font-custom-bold text-slate-800 mb-4">
                                                {selectedShatayushiMember.firstName + " " + selectedShatayushiMember.middleName}
                                            </h2>

                                            {/* Close Button */}
                                            <button
                                                className={`flex items-center justify-center rounded-lg
                                                    bg-[#F5F5F5] hover:bg-[#F7F7F7] aspect-square

                                                    portrait:hover:ring-4 landscape:hover:ring-2
                                                    landscape:jk:hover:ring-4 hover:ring-[#D4D4D4]
                                                    landscape:rounded-[0.2rem] landscape:jk:rounded-md
                                                    transition duration-300 ease-out

                                                    portrait:w-[2rem] portrait:md:w-[1.75rem]
                                                    portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]

                                                    landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                                    landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]`}
                                                onClick={() => {
                                                    setSelectedShatayushiMember({
                                                        age: 100,
                                                        description: "",
                                                        userId: "",
                                                        dateOfBirth: "",
                                                        dateOfDeath: "",
                                                        familyTreeName: "",
                                                        firstName: "",
                                                        middleName: "",
                                                        imageUrl: "",
                                                        imageUrlBg: "",
                                                    });
                                                    setShowShatayushiMemberModal(false);
                                                }}
                                            >
                                                <img
                                                    className={"w-[75%] h-[75%]"}
                                                    src={"/assets/close-icon.png"}
                                                    alt={"Close"}
                                                    title={"Close"}
                                                />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                            <div>
                                                <div className="flex items-center text-slate-600 mb-2">
                                                    <Calendar className="h-5 w-5 mr-2"/>
                                                    <span className="font-custom-regular">
                                                        {t("SHATAYUSHI_PAGE.LIFE_STORY_MODAL.BORN")}:
                                                    </span>
                                                </div>
                                                <p className="text-slate-800 ml-7 font-custom-medium">
                                                    {selectedShatayushiMember.dateOfBirth}
                                                </p>
                                            </div>

                                            <div>
                                                <div className="flex items-center text-slate-600 mb-2">
                                                    <Calendar className="h-5 w-5 mr-2"/>
                                                    <span className="font-custom-regular">
                                                        {t("SHATAYUSHI_PAGE.LIFE_STORY_MODAL.PASSED")}:
                                                    </span>
                                                </div>
                                                <p className="text-slate-800 ml-7 font-custom-medium">
                                                    {selectedShatayushiMember.dateOfDeath}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mb-6">
                                            <div className="flex items-center text-slate-600 mb-2">
                                                <Users className="h-5 w-5 mr-2"/>
                                                <span className="font-custom-regular">
                                                    {t("SHATAYUSHI_PAGE.LIFE_STORY_MODAL.FAMILY_TREE")}:
                                                </span>
                                            </div>
                                            <p className="text-slate-800 ml-7 font-custom-medium">
                                                {selectedShatayushiMember.familyTreeName}
                                            </p>
                                        </div>

                                        <div>
                                            <h3 className="text-lg font-custom-semi-bold text-slate-800 mb-3">
                                                {t("SHATAYUSHI_PAGE.LIFE_STORY_MODAL.LIFE_STORY")}
                                            </h3>
                                            <p className="text-slate-700 leading-relaxed font-custom-medium">
                                                {selectedShatayushiMember.description}
                                            </p>
                                        </div>

                                        <div className="mt-8 pt-6 border-t border-slate-200 text-center">
                                            <div
                                                className="inline-flex items-center
                                                    bg-amber-50 px-6 py-3 rounded-full"
                                            >
                                                <Star className="h-5 w-5 text-amber-600 mr-2"/>
                                                <span className="text-slate-800 font-custom-medium-italic">
                                                    {selectedShatayushiMember.age} {t("SHATAYUSHI_PAGE.LIFE_STORY_MODAL.AGE_MESSAGE")}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </TransitionChild>
                    </div>
                </Transition>
            </>
        </>
    );
};
